import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCountry } from 'Context/CountryContext';
import { countryName } from 'Utils/euCountries';
import * as S from './CountrySelect.style';

const CountrySelect: React.FC<{ dropUp?: boolean }> = ({ dropUp = false }) => {
  const { i18n } = useTranslation();
  const { country, setCountry, countries } = useCountry();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  if (!countries || countries.length === 0) return null;

  const lang = i18n.language || 'de';

  return (
    <S.Wrapper ref={ref} data-testid="country-select">
      <S.SelectButton
        onClick={() => setOpen((v) => !v)}
        aria-label="Lieferland wählen"
        aria-expanded={open}
        data-testid="country-select-button"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
        </svg>
        {country === 'NON_EU' ? (lang.startsWith('en') ? 'Non-EU' : 'Nicht-EU') : country}
        <svg
          viewBox="0 0 24 24"
          width="12"
          height="12"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          style={{ transition: 'transform 0.2s ease', transform: open ? 'rotate(180deg)' : 'none' }}
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </S.SelectButton>

      {open && (
        <S.Dropdown $dropUp={dropUp} data-testid="country-select-dropdown">
          {countries.map((c) => (
            <S.Item
              key={c.code}
              $active={c.code === country}
              onClick={() => { setCountry(c.code); setOpen(false); }}
              data-testid={`country-option-${c.code}`}
            >
              <span className="name">{countryName(c.code, lang)}</span>
              <span className="rate">
                {c.code === 'NON_EU'
                  ? (lang.startsWith('en') ? 'net' : 'netto')
                  : `${(c.ratePermille / 10).toLocaleString(lang.startsWith('en') ? 'en' : 'de')} %`}
              </span>
            </S.Item>
          ))}
        </S.Dropdown>
      )}
    </S.Wrapper>
  );
};

export default CountrySelect;
